// Display ordering for the article list.
//
// The server returns `folder.asc,title.asc`, which is wrong for the folders that
// hold versioned documents (release notes, summary reports): a string sort puts
// "7.10" *before* "7.9", and even when it doesn't, the newest release is the one
// the reader wants first. So within each folder we detect version-numbered
// titles and sort those newest-first; everything else keeps the server's order.

/** First dotted number in a title: "Release Notes 7.10" → [7, 10]. */
const VERSION_RE = /(\d+(?:\.\d+)+)/;

export function parseVersion(title: string): number[] | null {
  const m = title.match(VERSION_RE)?.[1];
  return m ? m.split(".").map(Number) : null;
}

/** Descending compare of dotted version tuples ([7,10] before [7,9]). */
function compareDesc(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (b[i] ?? 0) - (a[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * Order one folder's articles for display: version-numbered titles first,
 * newest → oldest; the rest after, in the order they came in (title.asc).
 * A folder with fewer than two versioned titles is left untouched — one stray
 * number in an otherwise alphabetical folder shouldn't reshuffle it.
 */
export function orderFolderItems<T extends { title: string }>(items: T[]): T[] {
  const versioned: Array<{ item: T; version: number[] }> = [];
  const plain: T[] = [];
  for (const item of items) {
    const version = parseVersion(item.title);
    if (version) versioned.push({ item, version });
    else plain.push(item);
  }
  if (versioned.length < 2) return items;
  versioned.sort((a, b) => compareDesc(a.version, b.version));
  return [...versioned.map((v) => v.item), ...plain];
}
